import Button from "../../../../base/button/button";
import React, {useCallback, useEffect, useState} from "react";
import {useDispatch} from "react-redux";
import useIsMounted from "../../../../../hooks/use-is-mounted";
import Api from "../../../../../../core/services/api/api";
import {useNotebookConfig} from "../../../../../hooks/use-notebook-config";
import {setReduxDialogState} from "../../../../../redux/slices/dialogs/actions";
import ReduxStoreRoutes from "../../../../../../core/models/static/routes/redux-store-routes";
import {ReactComponent as NoNotesFound} from "../../../../../../assets/images/art-like/no-notes-found.svg"
import NotebookNoteCard from "../../../notebook-note-card";

const FloatingActionButtonNotebookContentNotes = () => {
    const [loading, setLoading] = useState(true);
    const [notes, setNotes] = useState([]);
    const dispatch = useDispatch();
    const {notesConfig} = useNotebookConfig();
    const isMounted = useIsMounted();

    /**
     * Fetches the notes for the current page url
     * @type {(function(*[]): Promise<[]>)}
     */
    const getTagsForTagGroup = useCallback(async (tagGroup) => {
        const size = 20;
        const _response = await Api.getPageNotes({
            tags: tagGroup,
            pagination: {currentPage: 1, pageSize: size}
        });
        if (!isMounted())
            return []
        if (_response?.resultFlag) {
            if (_response?.data?.totalCount > size) {
                const response = await Api.getPageNotes({
                    tags: tagGroup,
                    pagination: {currentPage: 1, pageSize: Math.max(_response?.data?.totalCount ?? 0, 1)}
                });
                if (!isMounted())
                    return []
                return response?.data?.items ?? []
            } else {
                return _response?.data?.items ?? []
            }
        }
        return []
    }, [isMounted])


    /**
     * Fetches the notes for the current page url
     * @type {(function(): Promise<void>)}
     */
    const getNotesForThisPage = useCallback(async () => {
        setLoading(true);
        const promises = notesConfig.tagGroups.map(e => getTagsForTagGroup(e));
        const groupsNotes = await Promise.all(promises)
        if (!isMounted())
            return;
        setNotes(groupsNotes.flat());
        setLoading(false);
    }, [getTagsForTagGroup, isMounted, notesConfig.tagGroups])

    /**
     * With initial render of the component and changes in the location:
     * Calls the function responsible for getting the list of notes
     */
    useEffect(() => {
        getNotesForThisPage().then();
    }, [getNotesForThisPage])

    /**
     * Opens the dialog for adding a new note.
     * @return {Promise<void>}
     */
    const addNewNote = useCallback(async () => {
        dispatch(setReduxDialogState(ReduxStoreRoutes.dialogs.upsertNote,
            {
                open: true,
                note: null,
                tagList: notesConfig.tagGroups[0],
                onSuccess: (note) => {
                    notesConfig.onNoteUpsert?.(note);
                    setNotes((prevState) => [note, ...prevState])
                },
            }))
    }, [dispatch, notesConfig])

    /**
     * Toggles the note's isPrivate property
     * @type {(function(*, *): Promise<void>)}
     */
    const toggleNoteIsPrivate = useCallback(async (note, isPrivate) => {
        const forApi = {
            noteId: note.id,
            title: note.title,
            body: note.body,
            isPrivate: isPrivate,
            tagList: note.tagList,
            updatedProperties: [
                "IsPrivate",
            ]
        }
        const response = await Api.updateNote(forApi);
        if (!isMounted())
            return;
        if (response?.resultFlag) {
            setNotes(prevState => {
                const result = prevState.map(e => e.id === note.id ? {...e, isPrivate: isPrivate} : e)
                notesConfig.onNoteUpsert?.(result.find(e => e.id === note.id));
                return result;
            });
        }
        return response;
    }, [isMounted, notesConfig])

    /**
     * Removes the note with the received [noteId]
     * @type {(function(*): Promise<void>)}
     */
    const removeNote = useCallback(async (noteId) => {
        const response = await Api.removeNote(noteId);
        if (!isMounted())
            return;
        if (response?.resultFlag) {
            setNotes(prevState => {
                notesConfig.onNoteUpsert?.({id: noteId}, true);
                return prevState.filter(e => e.id !== noteId)
            });
        }
    }, [isMounted, notesConfig])

    /**
     * Sets the note with the received [noteId] to the received [callbackOrTask]
     * @type {(function(number, function | any): void)}
     */
    const setNote = useCallback((noteId, callbackOrTask) => {
        setNotes(prevState => {
            let result = [];
            if (typeof callbackOrTask === 'function') {
                const found = prevState.find(e => e.id === noteId);
                if (!found)
                    return prevState;
                result = prevState.map(e => e.id === noteId ? callbackOrTask(e) : e);
            } else {

            }
            result = prevState.map(e => e.id === noteId ? callbackOrTask : e);
            notesConfig.onNoteUpsert?.(result.find(e => e.id === noteId));
            return result;
        })
    }, [notesConfig])


    return (
        <div className={'floaty-notebook-panel-content'}>
            <div className={'floaty-notebook-panel-scroll'}>
                {
                    loading
                        ? <div className={'notes-wrapper'}>
                            {
                                Array(10).fill(null).map((_, index) =>
                                    <div key={index} className={'loading normal mb-3 mx-2'}>
                                        <div/>
                                    </div>
                                )
                            }
                        </div>
                        : notes.length < 1
                            ? <div className={'floaty-notebook-empty-state'}>
                                <NoNotesFound/>
                            </div>
                            : <div className={'notes-wrapper'}>
                                {
                                    notes.map(e =>
                                        <NotebookNoteCard
                                            key={e.id}
                                            note={e}
                                            toggleIsPrivate={toggleNoteIsPrivate}
                                            removeNote={removeNote}
                                            setNote={setNote}
                                        />
                                    )
                                }
                            </div>
                }
            </div>
            <div className={'w-100 d-flex'}>
                <Button
                    disabled={loading}
                    className={'add-button'}
                    color={'primary'}
                    onClick={addNewNote}
                >
                    Add a new note
                </Button>
            </div>
        </div>
    );
}

export default FloatingActionButtonNotebookContentNotes;
